import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { dbHelpers } from '../../lib/supabase';

interface Space {
  id: string;
  space_type: string;
  location: string;
  size: string;
  price: string;
  status: string;
  description?: string;
  images?: string[];
  created_at: string;
}

export default function Spaces() {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingSpace, setEditingSpace] = useState<Space | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    space_type: '',
    location: '',
    size: '',
    price: '',
    status: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);

  // Giriş kontrolü
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // Kullanıcının ilanlarını yükle
  useEffect(() => {
    if (user) {
      loadUserSpaces();
    }
  }, [user]);

  const loadUserSpaces = async () => {
    setLoading(true);
    const { data, error } = await dbHelpers.getUserSpaces();
    
    if (error) {
      console.error('İlanlar yüklenirken hata:', error);
      setLoading(false);
      return;
    }

    if (data) {
      setSpaces(data);
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const handleEditClick = (space: Space) => {
    setEditingSpace(space);
    setEditFormData({
      space_type: space.space_type,
      location: space.location,
      size: space.size,
      price: space.price,
      status: space.status,
      description: space.description || ''
    });
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingSpace(null);
    setEditFormData({
      space_type: '',
      location: '',
      size: '',
      price: '',
      status: '',
      description: ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editingSpace) return;

    setSaving(true);
    const { error } = await dbHelpers.updateSpace(editingSpace.id, editFormData);

    if (error) {
      console.error('Alan güncellenirken hata:', error);
      alert('Alan güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
      setSaving(false);
      return;
    }

    // Listeyi yenile
    await loadUserSpaces();
    setSaving(false);
    handleCloseModal();
  };

  const handleDeleteSpace = async (spaceId: string) => {
    if (!confirm('Bu alanı silmek istediğinizden emin misiniz?')) {
      return;
    }

    const { error } = await dbHelpers.deleteSpace(spaceId);

    if (error) {
      console.error('Alan silinirken hata:', error);
      alert('Alan silinirken bir hata oluştu. Lütfen tekrar deneyin.');
      return;
    }

    // Listeyi yenile
    await loadUserSpaces();
  };

  const getSpaceTypeText = (type: string) => {
    const types: { [key: string]: string } = {
      'otopark': 'Otopark',
      'depo': 'Depo/Ambar',
      'garaj': 'Garaj',
      'bahce': 'Bahçe',
      'diger': 'Diğer'
    };
    return types[type] || type;
  };

  const getStatusText = (status: string) => {
    return status === 'rented' ? 'Kiralandı' : 'Müsait';
  };

  const getStatusColor = (status: string) => {
    return status === 'rented' 
      ? 'bg-green-500/20 text-green-300 border-green-500/30' 
      : 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
  };

  const filteredSpaces = spaces.filter(space => {
    const matchesFilter = filter === 'all' || space.status === filter;
    const matchesSearch = space.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         getSpaceTypeText(space.space_type).toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#121C2E' }}>
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-orange-500 animate-spin"></i>
          <p className="mt-4 text-white/70">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#121C2E' }}>
      {/* Floating Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-blue-400/10 rounded-full blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-white/5 rounded-full blur-xl animate-pulse delay-2000"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-white/10 backdrop-blur-md shadow-sm sticky top-0 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-white cursor-pointer">
                pad<span className="text-orange-500">baq</span>
              </Link>
            </div>
            <nav className="flex items-center space-x-4">
              <Link to="/dashboard" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Panel</Link>
              <Link to="/spaces" className="text-orange-500 font-medium px-4 py-2 rounded-lg bg-white/10 whitespace-nowrap cursor-pointer">Alanlarım</Link>
              <Link to="/boxes" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Koli Yönetimi</Link>
              <Link to="/profile" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Profil</Link>
              <button 
                onClick={handleLogout}
                className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-logout-circle-line mr-2"></i>
                Çıkış
              </button>
            </nav>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Alanlarım</h1>
            <p className="text-white/70">Tüm ilan ve kira durumlarınızı buradan yönetin</p>
          </div>
          <div className="mt-4 md:mt-0">
            <Link
              to="/spaces/new"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              Yeni Alan Ekle
            </Link>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-8 border border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <i className="ri-search-line text-white/50"></i>
              </div>
              <input
                type="text"
                placeholder="Konum veya alan türü ile ara..."
                className="block w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm text-white placeholder-white/50"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filter */}
            <div className="flex space-x-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  filter === 'all'
                    ? 'bg-orange-500 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                }`}
              >
                Tümü ({spaces.length})
              </button>
              <button
                onClick={() => setFilter('available')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  filter === 'available'
                    ? 'bg-orange-500 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                }`}
              >
                Müsait ({spaces.filter(s => s.status === 'available').length})
              </button>
              <button
                onClick={() => setFilter('rented')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  filter === 'rented'
                    ? 'bg-orange-500 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                }`}
              >
                Kiralandı ({spaces.filter(s => s.status === 'rented').length})
              </button>
            </div>
          </div>
        </div>

        {/* Spaces Grid */}
        {filteredSpaces.length === 0 ? (
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg border border-white/10">
            <div className="text-center py-16">
              <i className="ri-file-list-3-line text-6xl text-white/20 mb-4"></i>
              {spaces.length === 0 ? (
                <>
                  <h3 className="text-xl font-medium text-white mb-2">Henüz alan yok</h3>
                  <p className="text-white/60 mb-8">İlk ilanınızı oluşturarak başlayın!</p>
                  <Link 
                    to="/spaces/new"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line mr-2"></i>
                    İlk Alanımı Ekle
                  </Link>
                </>
              ) : (
                <>
                  <h3 className="text-xl font-medium text-white mb-2">Sonuç bulunamadı</h3>
                  <p className="text-white/60">Arama kriterlerinizi değiştirip tekrar deneyin</p>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSpaces.map((space) => (
              <div key={space.id} className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-white/10 group">
                {/* Space Image */}
                <div className="relative overflow-hidden rounded-t-2xl">
                  <img 
                    src={space.images && space.images.length > 0 
                      ? space.images[0]
                      : space.space_type === 'otopark' 
                        ? "https://public.readdy.ai/ai/img_res/b5f7f9afce7518df3c6051688186a24b.jpg"
                        : "https://readdy.ai/api/search-image?query=Clean%20organized%20storage%20warehouse%20space%20modern%20facility%20rental%20solutions%20professional%20lighting%20secure%20storage%20environment%20commercial%20warehouse%20interior&width=400&height=200&seq=space002&orientation=landscape"
                    }
                    alt={getSpaceTypeText(space.space_type)}
                    className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(space.status)}`}>
                      {getStatusText(space.status)}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <span className="px-3 py-1 text-xs font-medium bg-black/50 text-white rounded-full backdrop-blur-sm">
                      {getSpaceTypeText(space.space_type)}
                    </span>
                  </div>
                  {space.images && space.images.length > 1 && (
                    <div className="absolute bottom-4 right-4">
                      <span className="px-2 py-1 text-xs font-medium bg-black/50 text-white rounded-full backdrop-blur-sm flex items-center">
                        <i className="ri-image-line mr-1"></i>
                        {space.images.length}
                      </span>
                    </div>
                  )}
                </div>

                {/* Space Details */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-white line-clamp-2">{space.location}</h3>
                    <div className="text-right">
                      <div className="text-lg font-bold text-orange-400">{space.price}</div>
                      <div className="text-xs text-white/60">aylık</div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-white/70">
                      <i className="ri-ruler-line text-white/50 mr-2"></i>
                      <span>{space.size}</span>
                    </div>
                    <div className="flex items-center text-sm text-white/70">
                      <i className="ri-calendar-line text-white/50 mr-2"></i>
                      <span>{new Date(space.created_at).toLocaleDateString('tr-TR')}</span>
                    </div>
                  </div>

                  {space.description && (
                    <p className="text-sm text-white/60 mb-4 line-clamp-2">
                      {space.description}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => handleEditClick(space)}
                      className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-edit-line mr-1"></i>
                      Düzenle
                    </button>
                    <button className="flex-1 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-eye-line mr-1"></i>
                      Görüntüle
                    </button>
                    <button 
                      onClick={() => handleDeleteSpace(space.id)}
                      className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Statistics */}
        {spaces.length > 0 && (
          <div className="mt-8 bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4">İstatistikler</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{spaces.length}</div>
                <div className="text-sm text-white/60">Toplam Alan</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">{spaces.filter(s => s.status === 'rented').length}</div>
                <div className="text-sm text-white/60">Kiralandı</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-400">{spaces.filter(s => s.status === 'available').length}</div>
                <div className="text-sm text-white/60">Müsait</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-400">
                  ₺{spaces.filter(s => s.status === 'rented').reduce((sum, space) => {
                    const price = parseInt(space.price.replace(/[^0-9]/g, '')) || 0;
                    return sum + price;
                  }, 0).toLocaleString()}
                </div>
                <div className="text-sm text-white/60">Aylık Gelir</div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a2942] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/10">
            {/* Modal Header */}
            <div className="sticky top-0 bg-[#1a2942] border-b border-white/10 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Alan Düzenle</h2>
              <button
                onClick={handleCloseModal}
                className="text-white/70 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Alan Türü */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Alan Türü <span className="text-red-400">*</span>
                </label>
                <select
                  value={editFormData.space_type}
                  onChange={(e) => setEditFormData({ ...editFormData, space_type: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white pr-8 cursor-pointer"
                  required
                >
                  <option value="otopark">Otopark</option>
                  <option value="depo">Depo/Ambar</option>
                  <option value="garaj">Garaj</option>
                  <option value="bahce">Bahçe</option>
                  <option value="diger">Diğer</option>
                </select>
              </div>

              {/* Konum */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Konum <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={editFormData.location}
                  onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                  placeholder="Örn: Kadıköy, İstanbul"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50"
                  required
                />
              </div>

              {/* Boyut */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Boyut <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={editFormData.size}
                  onChange={(e) => setEditFormData({ ...editFormData, size: e.target.value })}
                  placeholder="Örn: 20 m²"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50"
                  required
                />
              </div>

              {/* Fiyat */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Aylık Kira Bedeli <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={editFormData.price}
                  onChange={(e) => setEditFormData({ ...editFormData, price: e.target.value })}
                  placeholder="Örn: ₺2,500"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50"
                  required
                />
              </div>

              {/* Durum */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Durum <span className="text-red-400">*</span>
                </label>
                <select
                  value={editFormData.status}
                  onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white pr-8 cursor-pointer"
                  required
                >
                  <option value="available">Müsait</option>
                  <option value="rented">Kiralandı</option>
                </select>
              </div>

              {/* Açıklama */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Açıklama
                </label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  placeholder="Alan hakkında detaylı bilgi..."
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50 resize-none"
                />
                <p className="text-xs text-white/50 mt-1">{editFormData.description.length}/500 karakter</p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-[#1a2942] border-t border-white/10 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={handleCloseModal}
                disabled={saving}
                className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                İptal
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editFormData.space_type || !editFormData.location || !editFormData.size || !editFormData.price || !editFormData.status}
                className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg transition-all shadow-lg cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line mr-2"></i>
                    Kaydet
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Support Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button className="bg-gradient-to-r from-orange-500 to-orange-600 text-white w-14 h-14 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 flex items-center justify-center cursor-pointer">
          <i className="ri-chat-3-line text-xl"></i>
        </button>
      </div>
    </div>
  );
}